import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Phone, Mail, Globe, AtSign, Handshake, Star } from "lucide-react";
import api from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

export const BusinessCard = ({ b, index }) => {
  const navigate = useNavigate();
  return (
  <motion.article
    onClick={() => navigate(`/business/${b.id}`)}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.8, ease: EASE, delay: (index % 3) * 0.08 }}
    className="flex cursor-pointer flex-col border border-stone_warm bg-white p-6 shadow-[0_8px_30px_rgba(26,26,26,0.04)] transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-gold"
    data-testid={`business-card-${b.id}`}
    role="link"
  >
    {b.logo_path && (
      <img
        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${b.logo_path}`}
        alt={`${b.business_name} logo`}
        className="mb-5 h-14 w-14 rounded-full border border-stone_warm object-cover"
        data-testid={`business-logo-${b.id}`}
      />
    )}
    <div className="mb-4 flex items-center justify-between gap-3">
      <button
        onClick={(e) => { e.stopPropagation(); navigate(`/sector/${encodeURIComponent(b.sector)}`); }}
        className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-dark underline-offset-4 transition-colors hover:text-gold hover:underline"
        data-testid={`business-sector-link-${b.id}`}
      >
        {b.sector}
      </button>
      {b.featured ? (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/10 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-gold-dark" data-testid={`featured-badge-${b.id}`}>
          <Star size={10} fill="currentColor" /> Featured
        </span>
      ) : (
        <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold" aria-hidden="true" />
      )}
    </div>
    <h3 className="font-serif text-2xl font-medium tracking-tight text-charcoal">{b.business_name}</h3>
    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-charcoal/45">by {b.owner_name}</p>
    <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-charcoal/70">{b.description}</p>

    <div className="mt-5 space-y-3 border-t border-stone_warm pt-4 text-sm">
      <p className="text-charcoal/70"><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/45">Offers — </span>{b.offerings}</p>
      {b.needs && (
        <p className="flex items-start gap-2 text-charcoal/70">
          <Handshake size={14} className="mt-0.5 shrink-0 text-gold-dark" />
          <span><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/45">Seeks — </span>{b.needs}</span>
        </p>
      )}
    </div>

    <div className="mt-auto space-y-2 border-t border-stone_warm pt-4 text-xs text-charcoal/60">
      <p className="flex items-center gap-2"><MapPin size={13} className="text-gold-dark" />{b.location}</p>
      <p className="flex items-center gap-2"><Phone size={13} className="text-gold-dark" />{b.phone}</p>
      <p className="flex items-center gap-2 break-all"><Mail size={13} className="shrink-0 text-gold-dark" />{b.email}</p>
      {b.website && (
        <a href={b.website.startsWith("http") ? b.website : `https://${b.website}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 text-gold-dark underline-offset-4 transition-colors hover:text-gold hover:underline" data-testid={`business-website-${b.id}`}>
          <Globe size={13} />Visit website
        </a>
      )}
      {b.social && <p className="flex items-center gap-2"><AtSign size={13} className="text-gold-dark" />{b.social.replace(/^@/, "")}</p>}
    </div>
    <span className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-dark">View profile →</span>
  </motion.article>
  );
};

export const Directory = ({ refreshKey }) => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sector, setSector] = useState("All");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/businesses")
      .then((res) => { if (!cancelled) setBusinesses(res.data); })
      .catch(() => { if (!cancelled) setBusinesses([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const sectors = useMemo(
    () => ["All", ...new Set(businesses.map((b) => b.sector))],
    [businesses]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return businesses.filter((b) => {
      const matchesSector = sector === "All" || b.sector === sector;
      const matchesQuery = !q || [b.business_name, b.owner_name, b.description, b.offerings, b.location]
        .join(" ").toLowerCase().includes(q);
      return matchesSector && matchesQuery;
    });
  }, [businesses, query, sector]);

  return (
    <section id="directory" className="border-t border-stone_warm bg-[#F3F0EB]/60 py-28 lg:py-36" data-testid="directory-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-16 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between"
        >
          <div>
            <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-gold-dark">The Directory</p>
            <h2 className="font-serif text-4xl font-light tracking-tighter text-charcoal sm:text-5xl">
              Businesses of <span className="italic text-gold-dark">the Guild</span>
            </h2>
            <p className="mt-4 max-w-lg text-base text-charcoal/65" data-testid="directory-subtitle">
              Find a partner, a supplier, or a referral.
            </p>
          </div>
          <div className="relative w-full max-w-sm">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, service, or location…"
              className="w-full rounded-full border border-stone_warm bg-white py-3.5 pl-11 pr-4 text-sm text-charcoal placeholder-charcoal/35 outline-none transition-[border-color,box-shadow] duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20"
              data-testid="directory-search-input"
            />
          </div>
        </motion.div>

        <div className="mb-12 flex flex-wrap gap-2" data-testid="sector-filters">
          {sectors.map((s) => (
            <button
              key={s}
              onClick={() => setSector(s)}
              className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                sector === s
                  ? "border-charcoal bg-charcoal text-ivory"
                  : "border-stone_warm bg-white text-charcoal/60 hover:border-gold hover:text-gold-dark"
              }`}
              data-testid={`sector-filter-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="py-20 text-center font-mono text-xs uppercase tracking-[0.24em] text-charcoal/40" data-testid="directory-loading">
            Gathering the Guild…
          </p>
        ) : filtered.length === 0 ? (
          <p className="border border-dashed border-stone_warm bg-white/60 py-20 text-center font-serif text-2xl font-light italic text-charcoal/50" data-testid="directory-empty">
            No businesses found — be the first to register.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="directory-grid">
            {filtered.map((b, i) => <BusinessCard key={b.id} b={b} index={i} />)}
          </div>
        )}
      </div>
    </section>
  );
};
